import classNames from "classnames";
import React, { useCallback, useMemo } from "react";
import { Svg, SvgSize } from "../../../../Shared/Assets/Svg/React";

export enum ChevronDirection {
  Up = 0,
  Right = 1,
  Down = 2,
  Left = 4
}

interface ChevronButtonProps {
  id?: string;
  direction?: ChevronDirection;
  active?: boolean;
  disabled?: boolean;
  /**
   * Unavailable for now, e.g. while the calendar loads. Unlike `disabled`, the button keeps
   * focus (it's `aria-disabled`), so a keyboard user doesn't lose their place.
   */
  busy?: boolean;
  onClick?: () => void;
  className?: string;
  additionalClass?: string;
  /** Accessible label for screen readers */
  ariaLabel?: string;
  /** Title tooltip text */
  title?: string;
}

const ChevronButton = ({
  id,
  direction = ChevronDirection.Up,
  active = false,
  disabled = false,
  busy = false,
  onClick,
  className,
  additionalClass,
  ariaLabel,
  title
}: ChevronButtonProps) => {
  const inactive = disabled || busy;

  const buttonClasses = useMemo(
    () =>
      classNames(
        // Base button styles
        "inline-flex p-xs bg-neutral-200 rounded-tl-br-xs",
        "transition-short-out transition-short-in",
        "focus-visible:bg-neutral-200",
        "u-ripple u-ripple--dark",
        "relative overflow-hidden",
        "focus-outline",
        {
          // Active state
          "bg-dark-blue border-dark-blue": active,
          "hover:bg-dark-blue/80 hover:border-dark-blue/80": active,
          "focus-visible:bg-dark-blue/80 focus-visible:border-dark-blue/80": active,
          // Disabled state
          "cursor-not-allowed": inactive
        },
        className,
        additionalClass
      ),
    [active, inactive, className, additionalClass]
  );

  const svgClasses = useMemo(
    () =>
      classNames({
        "fill-dark-blue": !inactive,
        "fill-t-neutral-400": inactive,
        "rotate-90": direction === ChevronDirection.Right,
        "rotate-180": direction === ChevronDirection.Down,
        "-rotate-90": direction === ChevronDirection.Left
      }),
    [direction, inactive]
  );

  const handleClick = useCallback(() => {
    if (!inactive && onClick) {
      onClick();
    }
  }, [inactive, onClick]);

  return (
    <button
      id={id}
      type="button"
      className={buttonClasses}
      onClick={handleClick}
      disabled={disabled}
      aria-disabled={busy && !disabled ? true : undefined}
      aria-label={ariaLabel}
      title={title || ariaLabel}
    >
      <Svg
        href="/dist/sprite.svg#sys-chevron-up"
        size={SvgSize.LG}
        additionalClass={svgClasses}
      />
    </button>
  );
};

export default ChevronButton;
